import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Link,
  
} from '@mui/material';
import { Visibility as VisibilityIcon } from '@mui/icons-material';
import ApiService from '../../services/api';
import { useAuth } from '../../context/AuthContext';

export default function JobSeekerPublicProfile() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const preloaded = location.state?.seeker;
  const [user, setUser] = useState(preloaded || null);
  const [loading, setLoading] = useState(!preloaded);
  const [error, setError] = useState('');
  const [requestMsg, setRequestMsg] = useState('');
  const [adminSettings, setAdminSettings] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!id) return;
      try {
        setLoading(true);
        setError('');
        const res = await ApiService.getUserById(id);
        if (!mounted) return;
        setUser(res.user || res);
      } catch (e) {
        if (!mounted) return;
        // Fallback: keep preloaded state if available
        setError(e.message || 'Failed to load user profile');
      } finally {
        mounted && setLoading(false);
      }
    };
    if (!preloaded) {
      load();
    }
    return () => { mounted = false; };
  }, [id, preloaded]);

  // Aggressively load system settings from backend and keep local cache in sync
  useEffect(() => {
    let mounted = true;
    const loadSettings = async () => {
      try {
        const remote = await ApiService.getSystemSettings();
        if (!mounted) return;
        setAdminSettings(remote);
        try { localStorage.setItem('systemSettings', JSON.stringify(remote)); } catch (_) {}
        try { localStorage.setItem('adminSettings', JSON.stringify(remote)); } catch (_) {}
      } catch (e) {
        // Fallback to local storage only if backend fails
        try {
          const raw = localStorage.getItem('systemSettings') || localStorage.getItem('adminSettings');
          const parsed = raw ? JSON.parse(raw) : null;
          if (mounted) setAdminSettings(parsed);
        } catch (_) {}
      }
    };
    loadSettings();
    // Listen for settings updated events to update immediately across tabs
    const onUpdated = (ev) => {
      const next = ev?.detail || null;
      setAdminSettings(next);
    };
    window.addEventListener('systemSettingsUpdated', onUpdated);
    return () => {
      mounted = false;
      window.removeEventListener('systemSettingsUpdated', onUpdated);
    };
  }, []);

  // Build initials and avatar src like the directory
  const first = (user?.firstName || '').trim();
  const last = (user?.lastName || '').trim();
  const initialsBase = `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  const fallbackText = (user?.email || user?.username || '').toUpperCase();
  const initials = initialsBase || (fallbackText ? fallbackText.slice(0, 2) : 'NA');
  const avatarSrc = user?.avatar || '/logo192.png';
  const skills = user?.jobSeekerProfile?.skills || [];
  const headline = user?.jobSeekerProfile?.headline || user?.bio;
  const city = user?.jobSeekerProfile?.location?.city || user?.location?.city;
  const country = user?.jobSeekerProfile?.location?.country || user?.location?.country;
  const locationText = [city, country].filter(Boolean).join(', ');
  const education = user?.jobSeekerProfile?.education || [];
  const resumeUrl = user?.jobSeekerProfile?.resume || user?.cv?.fileUrl || '';
  const portfolio = Array.isArray(user?.jobSeekerProfile?.portfolio) ? user.jobSeekerProfile.portfolio : (user?.jobSeekerProfile?.portfolio ? [user.jobSeekerProfile.portfolio] : []);
  const expectedSalary = user?.jobSeekerProfile?.expectedSalary || {};
  const jobPreferences = user?.jobSeekerProfile?.jobPreferences || {};
  const social = user?.socialLinks || {};

  const showFullContact = (() => {
    const flag = adminSettings?.jobSeekerPrivacy?.showContactInfo;
    return typeof flag === 'boolean' ? flag : false; // default MASK until settings load
  })();

  const maskPhone = (phone) => {
    if (!phone || typeof phone !== 'string') return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    const head = digits.slice(0, 3);
    const masked = '*'.repeat(Math.max(digits.length - 3, 5));
    return `${head}${masked}`;
  };

  const maskEmail = (email) => {
    if (!email || typeof email !== 'string') return '';
    const [local] = email.split('@');
    const initials = (local || '').slice(0, 2) || 'em';
    return `${initials}@*************`;
  };

  const handleRequestContact = () => {
    try {
      if (!currentUser) {
        navigate('/login', { state: { from: location.pathname } });
        return;
      }
      setRequestMsg('Submitting contact request...');
      (async () => {
        try {
          await ApiService.createContactRequest(id);
          setRequestMsg('Contact request submitted');
        } catch (e) {
          setRequestMsg(e.message || 'Failed to submit request');
        } finally {
          setTimeout(() => setRequestMsg(''), 4000);
        }
      })();
    } catch (_) {}
  };

  useEffect(() => {
    let mounted = true;
    const checkStatus = async () => {
      if (!currentUser || !id) return;
      try {
        const res = await ApiService.getContactRequestStatus(id);
        if (!mounted) return;
        if (res.status === 'accepted' && res.contact) {
          setAdminSettings(prev => ({
            ...(prev || {}),
            jobSeekerPrivacy: { ...(prev?.jobSeekerPrivacy || {}), showContactInfo: true }
          }));
          setUser(u => ({ ...(u || {}), email: res.contact.email || u?.email, phone: res.contact.phone || u?.phone }));
        }
      } catch (_) {}
    };
    checkStatus();
    return () => { mounted = false; };
  }, [currentUser, id]);

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 8 }}>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {user && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar src={avatarSrc} sx={{ mr: 2, width: 56, height: 56 }}>
              {initials}
            </Avatar>
            <Box>
              <Typography variant="h5">{initials}</Typography>
              {locationText && (
                <Typography variant="body2" color="text.secondary">{locationText}</Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <VisibilityIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {(user?.profileViews ?? 0)} profile views
                </Typography>
              </Box>
            </Box>
          </Box>
          {headline && (
            <Typography variant="body1" sx={{ mb: 2 }}>
              {headline}
            </Typography>
          )}
          {skills.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {skills.map((skill) => (
                <Chip key={skill} label={skill} size="small" />
              ))}
            </Box>
          )}

          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Experience
            </Typography>
            <Grid container spacing={2}>
              {(user?.jobSeekerProfile?.experience || []).map((exp, idx) => (
                <Grid item xs={12} key={idx}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle1">{exp.position} @ {exp.company}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {exp.startDate ? new Date(exp.startDate).toLocaleDateString() : ''}
                      {exp.endDate ? ` - ${new Date(exp.endDate).toLocaleDateString()}` : ''}
                      {exp.current ? ' (Current)' : ''}
                    </Typography>
                    {exp.description && (
                      <Typography variant="body2" sx={{ mt: 1 }}>{exp.description}</Typography>
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Education */}
          {education.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Education
              </Typography>
              <Grid container spacing={2}>
                {education.map((edu, idx) => (
                  <Grid item xs={12} key={`edu-${idx}`}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="subtitle1">{edu.degree || edu.level} @ {edu.institution}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {edu.fieldOfStudy ? `${edu.fieldOfStudy} • ` : ''}
                        {edu.startDate ? new Date(edu.startDate).toLocaleDateString() : ''}
                        {edu.endDate ? ` - ${new Date(edu.endDate).toLocaleDateString()}` : ''}
                        {edu.current ? ' (Current)' : ''}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Resume */}
          {resumeUrl && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Resume
              </Typography>
              <Button variant="outlined" component="a" href={resumeUrl} target="_blank" rel="noopener noreferrer">
                View / Download Resume
              </Button>
            </Box>
          )}

          {/* Portfolio */}
          {portfolio.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Portfolio
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {portfolio.map((url, idx) => (
                  <Link key={`portfolio-${idx}`} href={url} target="_blank" rel="noopener noreferrer">
                    {url}
                  </Link>
                ))}
              </Box>
            </Box>
          )}

          {/* Job Preferences */}
          {(jobPreferences.jobTypes?.length || jobPreferences.workLocation?.length || jobPreferences.industries?.length || jobPreferences.desiredRole) && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Job Preferences
              </Typography>
              {jobPreferences.desiredRole && (
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Desired Role: {jobPreferences.desiredRole}
                </Typography>
              )}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {(jobPreferences.jobTypes || []).map((jt, i) => (
                  <Chip key={`jt-${i}`} label={jt} size="small" color="primary" variant="outlined" />
                ))}
                {(jobPreferences.workLocation || []).map((wl, i) => (
                  <Chip key={`wl-${i}`} label={wl} size="small" variant="outlined" />
                ))}
                {(jobPreferences.industries || []).map((ind, i) => (
                  <Chip key={`ind-${i}`} label={ind} size="small" variant="outlined" />
                ))}
              </Box>
            </Box>
          )}

          {/* Expected Salary */}
          {(expectedSalary.min != null || expectedSalary.max != null) && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Expected Salary
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {expectedSalary.currency || 'USD'}
                {expectedSalary.min != null ? `${expectedSalary.min.toLocaleString()}` : ''}
                {(expectedSalary.min != null && expectedSalary.max != null) ? ' - ' : ''}
                {expectedSalary.max != null ? `${expectedSalary.max.toLocaleString()}` : ''}
              </Typography>
            </Box>
          )}

          {/* Contact & Social */}
          {(user?.email || user?.phone || social.linkedin || social.github) && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Contact
              </Typography>
              {user?.email && (
                <Typography variant="body2">
                  Email: {showFullContact ? user.email : maskEmail(user.email)}
                </Typography>
              )}
              {user?.phone && (
                <Typography variant="body2">
                  Phone: {showFullContact ? user.phone : maskPhone(user.phone)}
                </Typography>
              )}
              {!showFullContact && (user?.email || user?.phone) && (
                <Box sx={{ mt: 1 }}>
                  <Button variant="contained" size="small" onClick={handleRequestContact}>
                    Request for contact
                  </Button>
                  {requestMsg && (
                    <Typography variant="caption" sx={{ ml: 1 }} color="text.secondary">
                      {requestMsg}
                    </Typography>
                  )}
                </Box>
              )}
              {(social.linkedin || social.github) && (
                <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {social.linkedin && (
                    <Link href={social.linkedin} target="_blank" rel="noopener noreferrer">LinkedIn</Link>
                  )}
                  {social.github && (
                    <Link href={social.github} target="_blank" rel="noopener noreferrer">GitHub</Link>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Paper>
      )}
    </Container>
  );
}